import { useCallback, useMemo, useRef } from 'react';
import { modals } from '@mantine/modals';

export type DirtyGuardLevel = 'Unternehmen' | 'Werk';

interface DirtyGuardSnapshot {
  level: DirtyGuardLevel;
  name: string;
  dirty: boolean;
}

export interface DirtyGuardApi {
  /** Von den Detail-Komponenten (CompanyDetail/PlantDetail) bei jedem Render aufgerufen -- haelt
   * fest, welche Ebene angezeigt wird und ob sie ungespeicherte Aenderungen traegt. Den
   * Vergleich liefert `form.isDirty()` der jeweiligen Ebene; der Guard rechnet nicht selbst. */
  track: (level: DirtyGuardLevel, name: string, dirty: boolean) => void;
  /** Beim Verlassen der Detail-Komponente (Unmount) aufgerufen, damit kein Reststand einer nicht
   * mehr angezeigten Ebene haengen bleibt. */
  clear: () => void;
  /** Fuehrt `next` sofort aus, wenn die zuletzt getrackte Ebene sauber ist; sonst fragt per
   * `ConfirmModal` nach (UI-SPEC Punkt 5). */
  requestChange: (next: () => void) => void;
}

/**
 * Schutz vor still verworfenen Aenderungen beim Ebenenwechsel (D-02, UI-SPEC Punkt 5).
 * Detail-Komponenten melden ueber `track`, ob ihre Ebene ungespeicherte Aenderungen hat;
 * `MasterDataPage` haengt jeden Auswahlwechsel (Baumklick, Enter im Baum, Sprung aus der
 * verlinkten Werkszeile) hinter `requestChange`.
 *
 * Der frueher hier beheimatete Wertevergleich ist entfallen: seit die Detailformulare ueber
 * `@mantine/form` laufen, beantwortet `form.isDirty()` dieselbe Frage gegen denselben
 * Ausgangsstand -- zwei Vergleiche fuer dieselbe Aussage waeren zwei Quellen, die auseinander
 * laufen koennen.
 */
export function useDirtyGuard(): DirtyGuardApi {
  const snapshotRef = useRef<DirtyGuardSnapshot | null>(null);

  const track = useCallback((level: DirtyGuardLevel, name: string, dirty: boolean) => {
    snapshotRef.current = { level, name, dirty };
  }, []);

  const clear = useCallback(() => {
    snapshotRef.current = null;
  }, []);

  const requestChange = useCallback((next: () => void) => {
    const snapshot = snapshotRef.current;
    if (!snapshot || !snapshot.dirty) {
      next();
      return;
    }
    modals.openConfirmModal({
      title: 'Änderungen verwerfen?',
      children: `Die Änderungen an ${snapshot.level} ${snapshot.name} sind nicht gespeichert. Beim Wechsel gehen sie verloren.`,
      labels: { confirm: 'Änderungen verwerfen', cancel: 'Abbrechen' },
      onConfirm: next,
    });
  }, []);

  // Memoisiert, damit Aufrufer (CompanyDetail/PlantDetail) die zurueckgegebene API gefahrlos in
  // einem useEffect-Dependency-Array referenzieren koennen, ohne bei jedem Render neu zu feuern.
  return useMemo(() => ({ track, clear, requestChange }), [track, clear, requestChange]);
}
